import {useMemo} from 'react';

import {
    hasValidPrice,
    VoucherPoolPrice,
    selectPools,
    useExamAvailability,
} from '@udemy/react-certification-vouchers';

import {VoucherPoolProductType} from 'src/generated/graphql';

type ExamAvailability = NonNullable<ReturnType<typeof useExamAvailability>>;

type VoucherPool = {
    id: string;
    hasAvailableVouchers: boolean;
    productType: VoucherPoolProductType;
};

interface UsePoolIdsForEventParams {
    exam: {
        id: string;
        offersRetakeInsurance: boolean;
        voucherPools?: Array<VoucherPool | null> | null;
        isAvailableForRegion: boolean;
    };
    voucherAvailability?: ExamAvailability;
    retakeInsurancePrice?: VoucherPoolPrice;
    defaultPrice?: VoucherPoolPrice;
    isVoucherPoolPricesLoading: boolean;
}

const getDefaultPoolId = (voucherAvailability?: ExamAvailability): string | undefined => {
    return voucherAvailability?.defaultPoolId;
};

const getRetakePoolId = (voucherAvailability?: ExamAvailability): string | undefined => {
    return voucherAvailability?.retakeInsurancePoolId;
};

const getPoolIdsWhileLoading = (exam: UsePoolIdsForEventParams['exam']): string[] => {
    const poolIds: string[] = [];
    const {defaultPool, retakeInsurancePool, hasBothPoolsAvailable} = selectPools(exam);

    if (hasBothPoolsAvailable) {
        if (defaultPool?.id) poolIds.push(defaultPool.id);
        if (retakeInsurancePool?.id) poolIds.push(retakeInsurancePool.id);
    } else if (defaultPool?.hasAvailableVouchers) {
        poolIds.push(defaultPool.id);
    } else if (retakeInsurancePool?.hasAvailableVouchers) {
        poolIds.push(retakeInsurancePool.id);
    }

    return poolIds.length > 0 ? poolIds : [exam.id];
};

const getPoolIdsWithPricing = (params: {
    exam: UsePoolIdsForEventParams['exam'];
    voucherAvailability?: ExamAvailability;
    defaultPrice?: VoucherPoolPrice;
    retakeInsurancePrice?: VoucherPoolPrice;
}): string[] => {
    const {exam, voucherAvailability, defaultPrice, retakeInsurancePrice} = params;
    const poolIds: string[] = [];
    const {defaultPool, retakeInsurancePool} = selectPools(exam);

    if (hasValidPrice(defaultPrice) && defaultPool?.hasAvailableVouchers) {
        const defaultPoolId = getDefaultPoolId(voucherAvailability);
        if (defaultPoolId) {
            poolIds.push(defaultPoolId);
        }
    }

    if (hasValidPrice(retakeInsurancePrice) && retakeInsurancePool?.hasAvailableVouchers) {
        const retakePoolId = getRetakePoolId(voucherAvailability);
        if (retakePoolId) {
            poolIds.push(retakePoolId);
        }
    }

    if (poolIds.length === 0) {
        return getFallbackPoolIds(exam, defaultPrice, retakeInsurancePrice);
    }

    return poolIds;
};

const getFallbackPoolIds = (
    exam: UsePoolIdsForEventParams['exam'],
    defaultPrice?: VoucherPoolPrice,
    retakeInsurancePrice?: VoucherPoolPrice,
): string[] => {
    const {defaultPool, retakeInsurancePool} = selectPools(exam);

    if (!defaultPrice && !retakeInsurancePrice) {
        if (defaultPool?.hasAvailableVouchers) {
            return [defaultPool.id];
        }
        if (retakeInsurancePool?.hasAvailableVouchers) {
            return [retakeInsurancePool.id];
        }
    }

    return [exam.id];
};

export const usePoolIdsForEvent = ({
    exam,
    voucherAvailability,
    retakeInsurancePrice,
    defaultPrice,
    isVoucherPoolPricesLoading,
}: UsePoolIdsForEventParams): string[] => {
    return useMemo(() => {
        if (!exam.offersRetakeInsurance) {
            const defaultPoolId =
                getDefaultPoolId(voucherAvailability) ??
                voucherAvailability?.voucherPoolId ??
                exam.id;
            return [defaultPoolId];
        }

        if (isVoucherPoolPricesLoading) {
            return getPoolIdsWhileLoading(exam);
        }

        return getPoolIdsWithPricing({
            exam,
            voucherAvailability,
            defaultPrice,
            retakeInsurancePrice,
        });
    }, [exam, voucherAvailability, isVoucherPoolPricesLoading, defaultPrice, retakeInsurancePrice]);
};
